import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RaceRecording, ReplayManifest } from '../../shared/race';
import { assembleSession, bufferedRanges, chunkIndex } from '../../shared/session-replay';
import { errorMessage } from '../api';
import { fetchChunk } from '../features/replayRequests';

export function useArchive() {
  const [manifest, setManifest] = useState<ReplayManifest | null>(null);
  const [chunks, setChunks] = useState(new Map<number, RaceRecording>());
  const [pending, setPending] = useState<number[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const cache = useRef(chunks), failures = useRef(new Set<number>()), cursor = useRef(0), generation = useRef(0);
  const requests = useRef(new Map<number, AbortController>());

  const cancel = useCallback(() => {
    generation.current++;
    for (const controller of requests.current.values()) controller.abort();
    requests.current.clear(); setPending([]);
  }, []);
  useEffect(() => () => { for (const controller of requests.current.values()) controller.abort(); }, []);

  const install = useCallback((next: ReplayManifest, first: RaceRecording, atMs: number) => {
    cancel(); const index = chunkIndex(next, atMs); cursor.current = index;
    cache.current = new Map([[index, first]]); failures.current.clear(); setErrors({});
    setChunks(cache.current); setManifest(next);
  }, [cancel]);

  const load = useCallback(async (index: number) => {
    if (!manifest || cache.current.has(index) || requests.current.has(index) || failures.current.has(index)) return;
    if (index < 0 || index * manifest.chunkDurationMs >= manifest.durationMs) return;
    const controller = new AbortController(), activeGeneration = generation.current;
    const timeout = setTimeout(() => controller.abort('timeout'), 150_000);
    requests.current.set(index, controller); setPending([...requests.current.keys()]);
    try {
      const recording = await fetchChunk(manifest, index, controller.signal);
      if (controller.signal.aborted || activeGeneration !== generation.current) return;
      // Loaded facts are part of the session ledger. Keep them when the cursor
      // moves; request priority may remain local, but seeking must never delete
      // the evidence required to rebuild a continuous view.
      cache.current = new Map(cache.current).set(index, recording); setChunks(cache.current);
      setErrors(previous => { const next = { ...previous }; delete next[index]; return next; });
    } catch (error) {
      if (activeGeneration === generation.current && (!controller.signal.aborted || controller.signal.reason === 'timeout')) {
        failures.current.add(index); setErrors(previous => ({ ...previous, [index]: controller.signal.reason === 'timeout' ? '此段数据加载超时，请重试。' : errorMessage(error) }));
      }
    } finally {
      clearTimeout(timeout);
      if (requests.current.get(index) === controller) { requests.current.delete(index); setPending([...requests.current.keys()]); }
    }
  }, [manifest]);

  const ensureAt = useCallback((atMs: number, rate: number, playing: boolean) => {
    if (!manifest) return;
    const index = chunkIndex(manifest, atMs); cursor.current = index;
    // A seek supersedes unrelated prefetches and keeps foreground requests responsive.
    let cancelled = false;
    for (const [key, controller] of requests.current) if (Math.abs(key - index) > 1) { controller.abort(); requests.current.delete(key); cancelled = true; }
    if (cancelled) setPending([...requests.current.keys()]);
    void load(index);
    if (playing && cache.current.has(index) && (index + 1) * manifest.chunkDurationMs - atMs <= Math.max(120_000, rate * 90_000)) void load(index + 1);
  }, [manifest, load]);
  const retry = useCallback((atMs: number) => { if (!manifest) return; const index = chunkIndex(manifest, atMs); failures.current.delete(index); setErrors(previous => { const next = { ...previous }; delete next[index]; return next; }); void load(index); }, [manifest, load]);
  const recording = useMemo(() => manifest ? assembleSession(manifest, chunks) : null, [manifest, chunks]);
  const ranges = useMemo(() => manifest ? bufferedRanges(manifest, chunks) : [], [manifest, chunks]);
  return { manifest, recording, ranges, pending, errors, install, ensureAt, retry, cancel };
}
